/**
 * Submit a game command via the game-platform API.
 *
 * Round-trips on the move critical path:
 *   1. GET events  — read the latest sequence number for optimistic concurrency
 *   2. POST submit — append the command
 *
 * The committed event (with its authoritative `userId`/`createdAt`) is pushed
 * to every participant over the WS event stream — that is exactly what
 * `SubmitCommandAck.delivery` signals. The synchronous result therefore only
 * needs to carry the assigned `sequenceNumber`, which is the sole field callers
 * read from it, so we synthesize the returned event from the ack instead of
 * issuing a third read-after-write GET.
 *
 * That removed GET was both redundant with the WS push and the source of
 * spurious `EVENT_READ_AFTER_WRITE_FAILED` errors when the just-written event
 * had not yet propagated to the node serving the read.
 */

import type {
  GamePlatformApiClient,
  GamePlatformEvent,
  SubmitCommandAck,
} from "@cloudnest/board-game-runtime";
import type { ApiResponse } from "../auth/types";
import type { GameEvent } from "../game-client";

export async function submitPlatformEvent(
  client: GamePlatformApiClient,
  roomId: string,
  gameType: string,
  eventType: string,
  payload: Record<string, unknown>,
): Promise<ApiResponse<GameEvent>> {
  const maxAttempts = 4;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const eventsBefore = await client.getEvents(roomId);
    if (!eventsBefore.success) return eventsBefore;

    const expectedSequenceNumber = latestSequenceNumber(eventsBefore.data);
    const clientCommandId = createClientCommandId(gameType, eventType);
    const submitResponse = await client.submitCommand(roomId, {
      clientCommandId,
      expectedSequenceNumber,
      eventType,
      payload,
    });
    if (!submitResponse.success) {
      if (
        submitResponse.errorCode === "EXPECTED_SEQUENCE_MISMATCH" &&
        attempt < maxAttempts - 1
      ) {
        continue;
      }
      return submitResponse;
    }

    return {
      success: true,
      data: ackToGameEvent(submitResponse.data, eventType, payload),
    };
  }

  return {
    success: false,
    error: "Failed to submit command after retry",
    errorCode: "EXPECTED_SEQUENCE_MISMATCH",
  };
}

function latestSequenceNumber(events: readonly GamePlatformEvent[]): number {
  return events.reduce(
    (latest, event) => Math.max(latest, event.sequenceNumber),
    0,
  );
}

function createClientCommandId(gameType: string, eventType: string): string {
  const suffix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${gameType}:${eventType}:${suffix}`;
}

/**
 * Build the GameEvent returned to callers from the submit acknowledgment.
 *
 * The ack does not carry `userId`/`createdAt` — those are server-owned fields
 * of the persisted event delivered over the WS stream. Callers only read
 * `sequenceNumber`, so the synchronous result intentionally leaves them empty.
 */
function ackToGameEvent(
  ack: SubmitCommandAck,
  eventType: string,
  payload: Record<string, unknown>,
): GameEvent {
  return {
    id: ack.eventId,
    gameId: ack.roomId,
    eventType,
    sequenceNumber: ack.sequenceNumber,
    userId: "",
    payload,
    createdAt: "",
  };
}
